import type React from "react";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Menu,
  MenuItem,
  IconButton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  ToggleButtonGroup,
  ToggleButton,
  type SelectChangeEvent,
} from "@mui/material";

import { HiDotsVertical } from "react-icons/hi";
import QueryDetailsModal from "../../common/QueryDetailsModal";
import toast from "react-hot-toast";
import TableSkeleton from "../../common/TableSkeleton";

export interface Query {
  _id: string;
  questionNumber: string;
  questionText: string;
  status: string;
  submittedDate: string;
  isDelete: boolean;
  answer?: string;
  answeredBy?: string;
}

export default function Dashboard() {
  const [question, setQuestion] = useState("");
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const userId = user?._id;
  const accessToken = user?.accessToken;

  const [loading, setLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [queries, setQueries] = useState<Query[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedQuery, setSelectedQuery] = useState<Query | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;
  const [filterStatus, setFilterStatus] = useState<"pending" | "responded">(
    "pending"
  );
  const [selectedQueryDetails, setSelectedQueryDetails] =
    useState<Query | null>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, query: Query) => {
      setAnchorEl(event.currentTarget);
      setSelectedQuery(query);
    },
    []
  );

  const fetchQueries = async () => {
    if (!userId || !accessToken) {
      toast.error("User authentication required");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/query/queries/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      if (res.status === 200) {
        setQueries(res.data.data.questions);
      }
    } catch (error) {
      console.error("Error fetching queries:", error);
      toast.error("Failed to fetch queries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId && accessToken) {
      fetchQueries();
    }
  }, [userId, accessToken]);

  const handleCreateQuestion = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!userId || !accessToken) {
      toast.error("User not authenticated.");
      return;
    }

    if (!question.trim()) {
      toast.error("Please enter a valid question.");
      return;
    }

    setIsSubmit(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/query/create`,
        { userId, questionText: question.trim() },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (res.status === 201) {
        toast.success(res.data.message || "Query created successfully!");
        setQuestion(""); // Clear the input field first
        await fetchQueries(); // Then fetch updated queries
      }
    } catch (error) {
      console.error("Error creating query:", error);
      toast.error("Failed to create query. Please try again.");
    } finally {
      setIsSubmit(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewQuery = (query: Query) => {
    setSelectedQueryDetails(query);
    setIsModalOpen(true);
    handleClose();
  };

  const handleEditQuery = (query: Query) => {
    handleClose();
  };

  const handleDeleteQuery = async (query: Query) => {
    if (!userId || !accessToken) {
      toast.error("User not authenticated");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/query/delete?queryId=${query._id}`,
        {
          data: {
            userId: userId,
            questionNumber: query.questionNumber,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("Query deleted successfully");
        await fetchQueries();
      }
    } catch (error) {
      console.error("Error deleting query:", error);
      toast.error("Failed to delete query. Please try again.");
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleDateFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value);
    setPage(1);
  };

  const getFilteredQueries = () => {
    let filtered = queries.filter((query) => {
      if (filterStatus === "pending") {
        return query.status === "pending";
      } else {
        return query.status === "responded";
      }
    });

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    switch (dateFilter) {
      case "today":
        filtered = filtered.filter((query) => {
          const queryDate = new Date(query.submittedDate);
          return queryDate.toDateString() === today.toDateString();
        });
        break;
      case "yesterday":
        filtered = filtered.filter((query) => {
          const queryDate = new Date(query.submittedDate);
          return queryDate.toDateString() === yesterday.toDateString();
        });
        break;
      case "lastWeek":
        filtered = filtered.filter((query) => {
          const queryDate = new Date(query.submittedDate);
          return queryDate >= lastWeek && queryDate <= today;
        });
        break;
      case "lastMonth":
        filtered = filtered.filter((query) => {
          const queryDate = new Date(query.submittedDate);
          return queryDate >= lastMonth && queryDate <= today;
        });
        break;
      case "oldest":
        filtered.sort(
          (a, b) =>
            new Date(a.submittedDate).getTime() -
            new Date(b.submittedDate).getTime()
        );
        break;
      case "newest":
        filtered.sort(
          (a, b) =>
            new Date(b.submittedDate).getTime() -
            new Date(a.submittedDate).getTime()
        );
        break;
      default:
        // 'all' - no additional filtering needed
        break;
    }

    return filtered;
  };

  const filteredQueries = getFilteredQueries();

  // Paginate the filtered queries
  const paginatedQueries = filteredQueries.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <div className="home px-2 sm:px-8 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between pt-4 pb-4">
        <h3 className="text-white font-semibold p-2">Query Submission Field</h3>
      </div>

      <div
        style={{ border: "2px solid rgba(54, 65, 95, 1)" }}
        className="mb-4 border-2 rounded-[16px] p-5"
      >
        <form onSubmit={handleCreateQuestion}>
          <textarea
            id="message"
            className="tarea rounded-[16px] p-2 w-full h-32 resize-none text-white focus:border-blue-500 focus:outline-none transition duration-300"
            placeholder="Type your query here..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          ></textarea>

          <button
            type="submit"
            className={`bg-blue-500 text-white text-base font-bold px-9 py-2 rounded-lg hover:bg-blue-600 transition duration-300 mt-4 w-full md:w-auto ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={loading}
          >
            {isSubmit ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>

      <div className="pt-4 pb-4">
        <h3 className="text-white font-semibold p-2 pb-8">
          Query Response History
        </h3>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <FormControl
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", sm: 200 },
              "& .MuiOutlinedInput-root": {
                color: "white",
                "& fieldset": {
                  borderColor: "#1E3359",
                },
                "&:hover fieldset": {
                  borderColor: "#1E3359",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1E3359",
                },
              },
              "& .MuiInputLabel-root": {
                color: "white",
              },
              "& .MuiSelect-icon": {
                color: "white",
              },
            }}
          >
            <InputLabel id="date-filter-label">Date Filter</InputLabel>
            <Select
              labelId="date-filter-label"
              value={dateFilter}
              label="Date Filter"
              onChange={handleDateFilterChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "white",
                    "& .MuiMenuItem-root": {
                      color: "black",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#e0e0e0",
                        "&:hover": {
                          backgroundColor: "#d5d5d5",
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="lastWeek">Last Week</MenuItem>
              <MenuItem value="lastMonth">Last Month</MenuItem>
              <MenuItem value="newest">Newest First</MenuItem>
              <MenuItem value="oldest">Oldest First</MenuItem>
            </Select>
          </FormControl>

          <div className="w-full sm:w-auto flex justify-center sm:justify-end">
            <ToggleButtonGroup
              value={filterStatus}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setFilterStatus(newValue);
                  setPage(1);
                }
              }}
              aria-label="query status filter"
              sx={{
                width: { xs: "100%", sm: "auto" },
                "& .MuiToggleButton-root": {
                  flex: { xs: 1, sm: "none" },
                  color: "white",
                  borderColor: "#1E3359",
                  "&.Mui-selected": {
                    backgroundColor: "#1E3359",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#16304f",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#16304f",
                  },
                },
              }}
            >
              <ToggleButton
                value="pending"
                aria-label="pending queries"
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                }}
              >
                Pending
              </ToggleButton>
              <ToggleButton
                value="responded"
                aria-label="responded queries"
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                }}
              >
                Responded
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>
        {loading ? (
          <TableSkeleton />
        ) : (
          <div className="relative flex flex-col w-full h-full rounded-[16px]">
            <div className="rounded-tl-[16px] rounded-tr-[16px] overflow-x-auto">
              <div>
                <table className="w-full table-auto min-w-max text-center custom_table">
                  <thead className="bg-[#1E3359]">
                    <tr>
                      <th className="p-4 border-b border-slate-600">
                        <p className="text-base font-bold leading-none text-white">
                          S.No
                        </p>
                      </th>
                      <th className="p-4 border-b border-slate-600">
                        <p className="text-base font-bold leading-none text-white">
                          Question Text
                        </p>
                      </th>
                      <th className="p-4 border-b border-slate-600 hidden md:table-cell">
                        <p className="text-base font-bold leading-none text-white">
                          Status
                        </p>
                      </th>
                      <th className="p-4 border-b border-slate-600 hidden md:table-cell">
                        <p className="text-base font-bold leading-none text-white">
                          Date Submitted
                        </p>
                      </th>
                      <th className="p-4 border-b border-slate-600">
                        <p className="text-base font-bold leading-none text-white">
                          Action
                        </p>
                      </th>
                    </tr>
                  </thead>

                  <tbody style={{ backgroundColor: "#ffffff0f" }}>
                    {paginatedQueries.length > 0 ? (
                      paginatedQueries.map((query, index) => (
                        <tr key={query._id} className="hover:bg-slate-700">
                          <td className="p-4 border-b">
                            <p className="text-sm text-white font-normal">
                              {(page - 1) * rowsPerPage + index + 1}
                            </p>
                          </td>
                          <td className="p-4 border-b">
                            <p className="text-sm text-white font-normal">
                              {query.questionText.slice(0, 12)}
                              {query.questionText.length > 12 ? "..." : ""}
                            </p>
                          </td>
                          <td className="p-4 border-b hidden md:table-cell">
                            <p className="text-sm text-white font-normal">
                              {query.status}
                            </p>
                          </td>
                          <td className="p-4 border-b hidden md:table-cell">
                            <p className="text-sm text-white font-normal">
                              {new Date(
                                query.submittedDate
                              ).toLocaleDateString()}
                            </p>
                          </td>
                          <td className="p-4 border-b text-center">
                            <IconButton
                              onClick={(e) => handleClick(e, query)}
                              className="text-white"
                            >
                              <HiDotsVertical className="cursor-pointer text-white" />
                            </IconButton>

                            {/* MUI Menu (Dropdown) */}
                            <Menu
                              anchorEl={anchorEl}
                              open={
                                Boolean(anchorEl) &&
                                selectedQuery?._id === query._id
                              }
                              onClose={handleClose}
                            >
                              <MenuItem
                                onClick={() => handleViewQuery(query)}
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "#1E3359",
                                  "&:hover": { backgroundColor: "#16304f" },
                                }}
                              >
                                View Query
                              </MenuItem>
                              <MenuItem
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "#1E3359",
                                  "&:hover": { backgroundColor: "#16304f" },
                                }}
                                onClick={() => handleEditQuery(query)}
                              >
                                Edit Query
                              </MenuItem>
                              <MenuItem
                                sx={{
                                  color: "#fff",
                                  backgroundColor: "#1E3359",
                                  "&:hover": { backgroundColor: "#16304f" },
                                }}
                                onClick={() => handleDeleteQuery(query)}
                              >
                                Delete Query
                              </MenuItem>
                            </Menu>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="p-4 text-white">
                          No queries found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="flex justify-center items-center py-4 text-white">
                  <Pagination
                    count={Math.ceil(filteredQueries.length / rowsPerPage)}
                    page={page}
                    onChange={handleChangePage}
                    color="primary"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "white",
                      },
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: "#1E3359",
                        color: "white",
                      },
                      "& .MuiPaginationItem-ellipsis, & .MuiPaginationItem-icon":
                        {
                          color: "white",
                        },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <QueryDetailsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        query={selectedQueryDetails}
      />
    </div>
  );
}
